/**
 * Deep, dependency-free CV diagnostics that sit alongside the weighted ATS
 * checks in `ats-engine.ts`.
 *
 * Everything here is *descriptive*: it reports what the document contains so
 * the UI can explain the score. No AI, no network, no mutation — safe to run
 * in the browser, on the server, and in tests.
 */

export type ContactDiagnostics = {
  fullName: string | null;
  email: string | null;
  phones: string[];
  city: string | null;
  linkedin: string | null;
  portfolio: string | null;
  warnings: string[];
};

export type LengthDiagnostics = {
  wordCount: number;
  paragraphCount: number;
  estimatedPages: number;
  readingTimeMinutes: number;
  profile: "graduate" | "experienced";
  targetRange: [number, number];
  verdict: "too-short" | "short" | "ideal" | "long" | "too-long";
};

export type SectionDiagnostics = {
  found: string[];
  missing: string[];
  recommended: string[];
};

export type DateDiagnostics = {
  ranges: { raw: string; startYear: number | null; endYear: number | null }[];
  gaps: string[];
  overlaps: string[];
  futureDates: string[];
  formatIssues: string[];
};

export type SkillBuckets = {
  hard: string[];
  soft: string[];
  computer: string[];
  industry: string[];
  legal: string[];
  compliance: string[];
  leadership: string[];
  language: string[];
};

export type BulletFinding = {
  text: string;
  quantified: boolean;
  verbLed: boolean;
  weakOpener: string | null;
  wordCount: number;
  suggestion?: string;
};

export type VerbDiagnostics = {
  used: string[];
  overused: { verb: string; count: number }[];
  weak: string[];
  recommended: string[];
};

export type GrammarDiagnostics = {
  issues: { type: string; detail: string; sample?: string }[];
  passiveCount: number;
  longSentences: number;
  repeatedWords: string[];
  readabilityGrade: number;
};

export type RiskDiagnostics = {
  flags: { id: string; label: string; severity: "critical" | "high" | "medium"; detail: string }[];
};

export type ReadabilityDiagnostics = {
  scanSeconds: number;
  bulletDensity: number;
  averageParagraphWords: number;
  longestParagraphWords: number;
  whitespaceRatio: number;
};

export type SaContextDiagnostics = {
  qualifications: string[];
  bodies: string[];
  nqfLevels: string[];
  sector: string | null;
  notes: string[];
};

export type AtsDiagnostics = {
  contact: ContactDiagnostics;
  length: LengthDiagnostics;
  sections: SectionDiagnostics;
  dates: DateDiagnostics;
  skills: SkillBuckets;
  bullets: BulletFinding[];
  verbs: VerbDiagnostics;
  grammar: GrammarDiagnostics;
  risks: RiskDiagnostics;
  readability: ReadabilityDiagnostics;
  saContext: SaContextDiagnostics;
};

/* ------------------------------------------------------------------ */
/* Vocabulary                                                          */
/* ------------------------------------------------------------------ */

export const RECOMMENDED_VERBS = [
  "Achieved","Implemented","Conducted","Developed","Designed","Led","Negotiated",
  "Reviewed","Drafted","Managed","Improved","Optimised","Analysed","Researched",
  "Delivered","Coordinated","Advised","Prepared","Reduced","Increased",
];

const WEAK_OPENERS = [
  "responsible for","duties included","worked on","helped with","assisted in",
  "tasked with","involved in","in charge of","participated in","was part of",
  "my role was","dealt with","handled",
];

const SKILL_LEXICON: { bucket: keyof SkillBuckets; terms: string[] }[] = [
  {
    bucket: "computer",
    terms: ["ms word","microsoft word","excel","powerpoint","outlook","sharepoint","sap","sage","pastel","xero","quickbooks","salesforce","hubspot","power bi","tableau","sql","python","javascript","canva","google workspace","teams","zoom","crm","erp","autocad","matlab","jira","confluence","adobe"],
  },
  {
    bucket: "legal",
    terms: ["legal research","legal drafting","litigation","conveyancing","contract drafting","contract law","labour law","ccma","high court","magistrate","pleadings","affidavit","due diligence","legal opinion","company secretarial","caselines","lexisnexis","juta","westlaw","oscola","moot","paralegal","notice of motion","summons","dispute resolution","mediation","arbitration"],
  },
  {
    bucket: "compliance",
    terms: ["popia","fica","ficaa","b-bbee","bbbee","king iv","companies act","cipc","aml","kyc","risk assessment","internal audit","governance","regulatory reporting","ohs","occupational health","iso 9001","gdpr","fais","fsca","sars","vat","paye","compliance monitoring"],
  },
  {
    bucket: "leadership",
    terms: ["team leadership","managed a team","mentoring","coaching","supervision","stakeholder management","project management","budget management","training delivery","performance management","change management","line management"],
  },
  {
    bucket: "soft",
    terms: ["communication","problem solving","attention to detail","time management","adaptability","collaboration","negotiation","critical thinking","client service","interpersonal","organised","resilience","emotional intelligence","conflict resolution"],
  },
  {
    bucket: "industry",
    terms: ["mining","banking","insurance","retail","logistics","supply chain","manufacturing","healthcare","education","public sector","municipal","telecoms","fintech","construction","agriculture","ngo","hospitality"],
  },
  {
    bucket: "language",
    terms: ["english","afrikaans","isizulu","zulu","isixhosa","xhosa","sesotho","sotho","setswana","tswana","sepedi","pedi","xitsonga","tsonga","tshivenda","venda","siswati","swati","isindebele","ndebele"],
  },
];

const SA_QUALIFICATIONS = [
  "llb","llm","bcom","ba law","bproc","national diploma","higher certificate",
  "advanced diploma","btech","ndip","matric","national senior certificate","nsc",
  "postgraduate diploma","pgdip","honours","bsc","beng","mba","diploma in law",
  "practical vocational training","pvt","legal practice council","admission exam",
];

const SA_BODIES = [
  "saica","saipa","cima","acca","hpcsa","sacap","ecsa","sanc","seta","merseta",
  "services seta","lpc","legal practice council","law society","sabpp","iodsa",
  "compliance institute","irba","fpi","sacnasp","samtrac",
];

const SECTOR_HINTS: { sector: string; terms: string[] }[] = [
  { sector: "legal", terms: ["llb","attorney","advocate","paralegal","litigation","conveyancing","legal"] },
  { sector: "finance", terms: ["saica","accountant","audit","bcom","financial","tax","bookkeep"] },
  { sector: "engineering", terms: ["ecsa","engineer","btech","mechanical","electrical","civil","autocad"] },
  { sector: "healthcare", terms: ["hpcsa","nurse","clinic","patient","pharmac","sanc","medical"] },
  { sector: "it", terms: ["developer","software","network","it support","sql","python","cyber"] },
  { sector: "hr", terms: ["sabpp","recruitment","human resources","payroll","onboarding","industrial relations"] },
  { sector: "government", terms: ["z83","municipal","department of","public service","government","spl"] },
];

const ALL_SECTIONS = [
  "professional summary","experience","education","skills","technical skills",
  "legal experience","projects","volunteer","achievements","awards",
  "certifications","languages","references","publications",
  "professional memberships","training",
];

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const MONTHS = "jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec";

function unique<T>(a: T[]): T[] {
  return Array.from(new Set(a));
}

function syllables(word: string): number {
  const w = word.toLowerCase().replace(/[^a-z]/g, "");
  if (!w) return 0;
  const m = w.match(/[aeiouy]{1,2}/g);
  return Math.max(1, m ? m.length : 1);
}

/* ------------------------------------------------------------------ */
/* Individual analysers                                                */
/* ------------------------------------------------------------------ */

export function analyseContact(text: string): ContactDiagnostics {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const head = lines.slice(0, 12);
  const warnings: string[] = [];

  const email = (text.match(/[\w.+-]+@[\w-]+\.[\w.-]+/) || [])[0] || null;
  const phones = unique(
    (text.match(/(?:\+27|0)\s?(?:\d[\s\-().]?){8,12}\d/g) || []).map((p) => p.trim()),
  ).slice(0, 4);
  const cityMatch = text.match(
    /\b(johannesburg|sandton|midrand|soweto|pretoria|centurion|durban|pietermaritzburg|cape town|stellenbosch|polokwane|bloemfontein|gqeberha|port elizabeth|east london|mbombela|nelspruit|kimberley|rustenburg|witbank|emalahleni|george|welkom|vereeniging|vanderbijlpark|thohoyandou|giyani|mahikeng|upington|richards bay)\b/i,
  );
  const provinceMatch = text.match(
    /\b(gauteng|limpopo|mpumalanga|kwazulu[- ]?natal|free state|north west|northern cape|western cape|eastern cape)\b/i,
  );
  const city = cityMatch?.[0] || provinceMatch?.[0] || null;
  const linkedin = (text.match(/(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/[\w-]+/i) || [])[0] || null;
  const portfolio =
    (text.match(/(?:https?:\/\/)?(?:www\.)?(?:github\.com|behance\.net|dribbble\.com|notion\.site|[\w-]+\.(?:co\.za|com|io|dev|me))\/?[\w\-/.]*/i) || [])
      .filter((u) => !/linkedin\.com|@/.test(u))[0] || null;

  // Full name: first non-empty line that looks like a person's name.
  const nameLine = head.find(
    (l) =>
      /^[A-Z][a-zA-Z'’.-]+(?:\s+[A-Z][a-zA-Z'’.-]+){1,3}$/.test(l) &&
      l.split(/\s+/).length <= 4 &&
      !/@|\d/.test(l),
  );
  const fullName = nameLine || null;

  if (!email) warnings.push("No email address found — most ATS reject a CV with no contact email.");
  if (email && /(hotmail|sexy|babe|cool|gangsta|420|69|xxx|princess|killer)/i.test(email))
    warnings.push(`"${email}" reads as informal. Use firstname.surname@ style for applications.`);
  if (email && /\d{4,}@/.test(email))
    warnings.push("Your email contains a long number string — a name-based address looks more professional.");
  if (!phones.length) warnings.push("No South African phone number detected (expected 0XX XXX XXXX or +27…).");
  if (phones.length > 1) warnings.push(`${phones.length} phone numbers listed — keep one reachable number to avoid parser confusion.`);
  if (!city) warnings.push("No city or province found. Recruiters filter by location; add e.g. 'Polokwane, Limpopo'.");
  if (!linkedin) warnings.push("No LinkedIn URL. A public profile raises recruiter response rates.");
  if (!fullName) warnings.push("Could not confidently detect your full name on the first lines — put it alone on line 1.");

  return { fullName, email, phones, city, linkedin, portfolio, warnings };
}

export function analyseLength(text: string, experienceYears: number): LengthDiagnostics {
  const wordCount = text.split(/\s+/).filter(Boolean).length;
  const paragraphs = text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  const estimatedPages = Math.max(1, Math.round((wordCount / 500) * 10) / 10);
  const readingTimeMinutes = Math.max(1, Math.round(wordCount / 220));
  const profile: LengthDiagnostics["profile"] = experienceYears >= 4 ? "experienced" : "graduate";
  const targetRange: [number, number] = profile === "experienced" ? [500, 1200] : [300, 900];

  let verdict: LengthDiagnostics["verdict"] = "ideal";
  if (wordCount < 250) verdict = "too-short";
  else if (wordCount < targetRange[0]) verdict = "short";
  else if (wordCount > 1800) verdict = "too-long";
  else if (wordCount > targetRange[1]) verdict = "long";

  return {
    wordCount,
    paragraphCount: paragraphs.length,
    estimatedPages,
    readingTimeMinutes,
    profile,
    targetRange,
    verdict,
  };
}

export function analyseSections(text: string): SectionDiagnostics {
  const lower = text.toLowerCase();
  const found = ALL_SECTIONS.filter((s) => {
    const head = s.split(" ")[0];
    return new RegExp(`(^|\\n)\\s*[^a-z\\n]{0,3}${head}[a-z ]{0,20}\\s*$`, "im").test(lower) ||
      new RegExp(`(^|\\n)\\s*${s}\\b`, "i").test(lower);
  });
  const essential = ["professional summary","experience","education","skills"];
  const missing = essential.filter((s) => !found.includes(s));
  const recommended = ["certifications","languages","achievements","references"].filter(
    (s) => !found.includes(s),
  );
  return { found, missing, recommended };
}

export function analyseDates(text: string): DateDiagnostics {
  const thisYear = new Date().getFullYear();
  const rangeRe = new RegExp(
    `((?:${MONTHS})[a-z]*\\.?\\s*)?((?:19|20)\\d{2})\\s*[–\\-—]{1,2}|\\bto\\b\\s*((?:${MONTHS})[a-z]*\\.?\\s*)?((?:19|20)\\d{2}|present|current)`,
    "gi",
  );
  void rangeRe;

  const raws =
    text.match(
      new RegExp(
        `(?:(?:${MONTHS})[a-z]*\\.?\\s*)?(?:19|20)\\d{2}\\s*(?:[–\\-—]|to)\\s*(?:(?:${MONTHS})[a-z]*\\.?\\s*)?(?:(?:19|20)\\d{2}|present|current|date)`,
        "gi",
      ),
    ) || [];

  const ranges = raws.map((raw) => {
    const yrs = (raw.match(/(19|20)\d{2}/g) || []).map(Number);
    const open = /present|current|date/i.test(raw);
    return {
      raw: raw.trim(),
      startYear: yrs[0] ?? null,
      endYear: open ? thisYear : (yrs[1] ?? null),
    };
  });

  const formatIssues: string[] = [];
  for (const raw of raws) {
    if (!new RegExp(`${MONTHS}`, "i").test(raw))
      formatIssues.push(`"${raw.trim()}" has no month — use "Mar 2022 – Aug 2024".`);
    if (/\b\d{1,2}\/\d{4}\b/.test(raw))
      formatIssues.push(`"${raw.trim()}" uses a numeric month; ATS parse "MMM YYYY" most reliably.`);
  }

  const futureDates = unique(
    (text.match(/\b(19|20)\d{2}\b/g) || []).filter((y) => Number(y) > thisYear + 1),
  ).map((y) => `${y} is in the future.`);

  const sorted = ranges
    .filter((r) => r.startYear && r.endYear)
    .sort((a, b) => (a.startYear! - b.startYear!));

  const gaps: string[] = [];
  const overlaps: string[] = [];
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    if (cur.startYear! - prev.endYear! >= 2)
      gaps.push(`Gap of about ${cur.startYear! - prev.endYear!} years between ${prev.endYear} and ${cur.startYear}.`);
    if (cur.startYear! < prev.endYear! - 0)
      overlaps.push(`"${prev.raw}" overlaps "${cur.raw}" — clarify if these ran concurrently.`);
  }

  return { ranges, gaps, overlaps, futureDates, formatIssues: unique(formatIssues).slice(0, 6) };
}

export function analyseSkills(text: string): SkillBuckets {
  const lower = text.toLowerCase();
  const buckets: SkillBuckets = {
    hard: [], soft: [], computer: [], industry: [],
    legal: [], compliance: [], leadership: [], language: [],
  };
  for (const { bucket, terms } of SKILL_LEXICON) {
    for (const term of terms) if (lower.includes(term)) buckets[bucket].push(term);
  }
  buckets.hard = unique([...buckets.computer, ...buckets.legal, ...buckets.compliance]).slice(0, 24);
  for (const k of Object.keys(buckets) as (keyof SkillBuckets)[]) buckets[k] = unique(buckets[k]);
  return buckets;
}

export function analyseBullets(text: string, powerVerbs: string[]): BulletFinding[] {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const bodies = lines
    .filter((l) => /^[-•*·▪●○]/.test(l))
    .map((l) => l.replace(/^[-•*·▪●○]\s*/, ""));

  return bodies.map((b) => {
    const lower = b.toLowerCase();
    const weakOpener = WEAK_OPENERS.find((w) => lower.startsWith(w)) || null;
    const verbLed = powerVerbs.some((v) => new RegExp(`^${v}\\b`, "i").test(b));
    const quantified =
      /(\bR\s?\d|\d+\s?%|\b\d{2,}\b|\b\d+\s*(years?|months?|clients?|matters?|files?|staff|people|reports?|cases?|hours?|days?)\b)/i.test(b);
    const wordCount = b.split(/\s+/).filter(Boolean).length;
    let suggestion: string | undefined;
    if (weakOpener)
      suggestion = `Replace "${weakOpener}" with an action verb (e.g. "${RECOMMENDED_VERBS[Math.floor(b.length) % RECOMMENDED_VERBS.length]}") and end with the outcome you actually achieved.`;
    else if (!verbLed) suggestion = "Start this bullet with a power verb so the impact leads.";
    else if (!quantified) suggestion = "Add a real volume, timeframe or rand value you can defend — never estimate.";
    else if (wordCount > 34) suggestion = "Split this into two bullets; recruiters skim past 34-word lines.";
    return { text: b, quantified, verbLed, weakOpener, wordCount, suggestion };
  });
}

export function analyseVerbs(bullets: BulletFinding[], powerVerbs: string[]): VerbDiagnostics {
  const counts = new Map<string, number>();
  for (const b of bullets) {
    const first = (b.text.split(/\s+/)[0] || "").toLowerCase().replace(/[^a-z]/g, "");
    if (powerVerbs.includes(first)) counts.set(first, (counts.get(first) || 0) + 1);
  }
  const used = Array.from(counts.keys());
  const overused = Array.from(counts.entries())
    .filter(([, n]) => n >= 3)
    .map(([verb, count]) => ({ verb, count }))
    .sort((a, b) => b.count - a.count);
  const weak = unique(bullets.map((b) => b.weakOpener).filter(Boolean) as string[]);
  const recommended = RECOMMENDED_VERBS.filter((v) => !used.includes(v.toLowerCase())).slice(0, 10);
  return { used, overused, weak, recommended };
}

export function analyseGrammar(text: string): GrammarDiagnostics {
  const issues: GrammarDiagnostics["issues"] = [];
  const sentences = text
    .split(/(?<=[.!?])\s+|\n/)
    .map((s) => s.trim())
    .filter((s) => s.split(/\s+/).length > 2);

  const passiveRe = /\b(was|were|is|are|been|being|be)\s+\w+(ed|en)\b/gi;
  const passiveHits = text.match(passiveRe) || [];
  if (passiveHits.length > 3)
    issues.push({
      type: "Passive voice",
      detail: `${passiveHits.length} passive constructions. Active voice reads stronger to recruiters.`,
      sample: passiveHits[0],
    });

  const longSentences = sentences.filter((s) => s.split(/\s+/).length > 34);
  if (longSentences.length)
    issues.push({
      type: "Sentence length",
      detail: `${longSentences.length} sentence(s) over 34 words.`,
      sample: longSentences[0].slice(0, 110),
    });

  const repeated = unique(
    (text.match(/\b(\w{3,})\s+\1\b/gi) || []).map((m) => m.split(/\s+/)[0].toLowerCase()),
  );
  if (repeated.length)
    issues.push({ type: "Repeated words", detail: `Doubled words: ${repeated.slice(0, 5).join(", ")}.` });

  const lowerStarts = text
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 3 && /^[a-z]/.test(l) && !/^[-•*]/.test(l));
  if (lowerStarts.length > 2)
    issues.push({
      type: "Capitalisation",
      detail: `${lowerStarts.length} lines start with a lowercase letter.`,
      sample: lowerStarts[0].slice(0, 80),
    });

  const doubleSpace = (text.match(/[a-z] {2,}[a-z]/gi) || []).length;
  if (doubleSpace > 3)
    issues.push({ type: "Spacing", detail: `${doubleSpace} places with double spaces between words.` });

  const punct = (text.match(/\s+[,.;:]/g) || []).length;
  if (punct > 2)
    issues.push({ type: "Punctuation", detail: `${punct} spaces before punctuation marks.` });

  const words = text.split(/\s+/).filter(Boolean);
  const sylTotal = words.reduce((s, w) => s + syllables(w), 0);
  const sentCount = Math.max(1, sentences.length);
  const readabilityGrade =
    Math.round(
      (0.39 * (words.length / sentCount) + 11.8 * (sylTotal / Math.max(1, words.length)) - 15.59) * 10,
    ) / 10;

  return {
    issues,
    passiveCount: passiveHits.length,
    longSentences: longSentences.length,
    repeatedWords: repeated,
    readabilityGrade: Math.max(0, readabilityGrade),
  };
}

export function analyseRisks(text: string): RiskDiagnostics {
  const flags: RiskDiagnostics["flags"] = [];
  const lines = text.split("\n");

  const tabCols = lines.filter((l) => /\t.*\t|\|\s.*\s\|/.test(l)).length;
  if (tabCols > 2)
    flags.push({ id: "tables", label: "Table / column layout", severity: "critical", detail: `${tabCols} lines look like table rows. ATS flatten tables and scramble reading order.` });

  const wideGaps = lines.filter((l) => /\S {6,}\S/.test(l)).length;
  if (wideGaps > 4)
    flags.push({ id: "columns", label: "Multiple columns", severity: "critical", detail: `${wideGaps} lines contain wide internal gaps — a sign of a two-column layout.` });

  if (/[\uFFFD]|\(cid:\d+\)/.test(text))
    flags.push({ id: "glyphs", label: "Unreadable characters", severity: "critical", detail: "The PDF text layer is corrupt or the fonts are not embedded — some ATS will read nothing at all." });

  if (/[\u{1F300}-\u{1FAFF}\u2600-\u27BF]/u.test(text))
    flags.push({ id: "emoji", label: "Emojis / icon glyphs", severity: "high", detail: "Emojis and icon fonts are stripped or become junk characters during parsing." });

  if (/\b(page \d+ of \d+|curriculum vitae\s*[-–|]\s*page)\b/i.test(text))
    flags.push({ id: "headers", label: "Header / footer content", severity: "high", detail: "Content inside headers and footers is frequently dropped by parsers." });

  if (/\b(text box|textbox|image|photo|picture|logo|graphic|watermark)\b/i.test(text))
    flags.push({ id: "graphics", label: "Images or graphics referenced", severity: "medium", detail: "Images, logos and watermarks carry no machine-readable text and can break parsing." });

  const bulletStyles = unique((text.match(/^[-•*·▪●○►]/gm) || []));
  if (bulletStyles.length > 2)
    flags.push({ id: "bullets", label: "Inconsistent bullet glyphs", severity: "medium", detail: `${bulletStyles.length} different bullet characters used. Standardise on one.` });

  const capsRuns = lines.filter((l) => l.trim().length > 60 && l === l.toUpperCase()).length;
  if (capsRuns > 1)
    flags.push({ id: "caps", label: "Long ALL-CAPS paragraphs", severity: "medium", detail: "ALL-CAPS body text hurts both parsing and human readability." });

  return { flags };
}

export function analyseReadability(text: string): ReadabilityDiagnostics {
  const lines = text.split("\n").map((l) => l.trim());
  const nonEmpty = lines.filter(Boolean);
  const bullets = nonEmpty.filter((l) => /^[-•*·▪●○]/.test(l)).length;
  const paragraphs = text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  const paraWords = paragraphs.map((p) => p.split(/\s+/).filter(Boolean).length);
  const words = text.split(/\s+/).filter(Boolean).length;

  return {
    scanSeconds: Math.max(4, Math.round(words / 45)),
    bulletDensity: nonEmpty.length ? Math.round((bullets / nonEmpty.length) * 100) : 0,
    averageParagraphWords: paraWords.length ? Math.round(paraWords.reduce((a, b) => a + b, 0) / paraWords.length) : 0,
    longestParagraphWords: paraWords.length ? Math.max(...paraWords) : 0,
    whitespaceRatio: lines.length ? Math.round(((lines.length - nonEmpty.length) / lines.length) * 100) : 0,
  };
}

export function analyseSaContext(text: string, role: string): SaContextDiagnostics {
  const lower = `${text}\n${role}`.toLowerCase();
  const qualifications = SA_QUALIFICATIONS.filter((q) => new RegExp(`\\b${q}\\b`, "i").test(lower));
  const bodies = SA_BODIES.filter((b) => lower.includes(b));
  const nqfLevels = unique((lower.match(/nqf\s*(level\s*)?\d{1,2}/gi) || []).map((s) => s.toUpperCase()));

  let sector: string | null = null;
  let best = 0;
  for (const { sector: s, terms } of SECTOR_HINTS) {
    const hits = terms.filter((t) => lower.includes(t)).length;
    if (hits > best) { best = hits; sector = s; }
  }

  const notes: string[] = [];
  if (sector === "legal")
    notes.push("Legal CVs: name the courts, statutes and document types you worked with, and list your PVT / LPC status explicitly.");
  if (sector === "finance")
    notes.push("Finance CVs: state your SAICA/SAIPA status, the accounting systems you use and the value of the books you handle.");
  if (sector === "engineering")
    notes.push("Engineering CVs: state ECSA registration status, project values and the standards/codes you work to.");
  if (sector === "healthcare")
    notes.push("Healthcare CVs: HPCSA/SANC registration number status, patient volumes and clinical settings are the first things screened.");
  if (sector === "it")
    notes.push("IT CVs: put the stack in a dedicated Technical Skills line — parsers match on exact technology names.");
  if (sector === "government")
    notes.push("Public-sector applications usually require a Z83 form plus a CV; keep the CV factual and mirror the advert's wording for the post requirements.");
  if (!qualifications.length)
    notes.push("No recognised South African qualification detected — spell out e.g. 'LLB' or 'National Diploma' in the Education section.");
  if (!nqfLevels.length && qualifications.length)
    notes.push("Add the NQF level next to your qualification; many public-sector and SETA-funded roles filter on it.");

  return { qualifications, bodies, nqfLevels, sector, notes };
}

/* ------------------------------------------------------------------ */
/* Composite                                                           */
/* ------------------------------------------------------------------ */

export function buildDiagnostics(
  text: string,
  role: string,
  powerVerbs: string[],
): AtsDiagnostics {
  const t = (text || "").replace(/\r/g, "");
  const dates = analyseDates(t);
  const spanYears = dates.ranges.reduce(
    (max, r) => (r.startYear && r.endYear ? Math.max(max, r.endYear - r.startYear) : max),
    0,
  );
  const totalYears = dates.ranges.reduce(
    (sum, r) => sum + (r.startYear && r.endYear ? Math.max(0, r.endYear - r.startYear) : 0),
    0,
  );
  const bullets = analyseBullets(t, powerVerbs);

  return {
    contact: analyseContact(t),
    length: analyseLength(t, Math.max(spanYears, totalYears)),
    sections: analyseSections(t),
    dates,
    skills: analyseSkills(t),
    bullets,
    verbs: analyseVerbs(bullets, powerVerbs),
    grammar: analyseGrammar(t),
    risks: analyseRisks(t),
    readability: analyseReadability(t),
    saContext: analyseSaContext(t, role),
  };
}
